import { FaceDetector, FilesetResolver } from '@mediapipe/tasks-vision';

let faceDetector = null;
let isInitializing = false;

/**
 * Inicializa el detector facial de MediaPipe
 * Se ejecuta una sola vez y se reutiliza en llamadas subsecuentes
 */
const initDetector = async () => {
  // Si ya está inicializado, retornarlo
  if (faceDetector) return faceDetector;

  // Si está inicializando, esperar
  if (isInitializing) {
    await new Promise((resolve) => {
      const checkInterval = setInterval(() => {
        if (faceDetector !== null) {
          clearInterval(checkInterval);
          resolve();
        }
      }, 100);
    });
    return faceDetector;
  }

  isInitializing = true;

  try {
    // Cargar archivos WASM desde carpeta public local
    const vision = await FilesetResolver.forVisionTasks('/mediapipe/wasm');

    // Crear detector con modelo BlazeFace local
    faceDetector = await FaceDetector.createFromOptions(vision, {
      baseOptions: {
        modelAssetPath: '/mediapipe/models/blaze_face_short_range.tflite',
      },
      runningMode: 'IMAGE',
      minDetectionConfidence: 0.85, // 85% de confianza mínima
      minSuppressionThreshold: 0.3, // Reduce detecciones superpuestas
    });

    isInitializing = false;
    return faceDetector;
  } catch (error) {
    isInitializing = false;
    console.error('Error al inicializar MediaPipe FaceDetector:', error);
    throw error;
  }
};

/**
 * Valida la relación de aspecto de una cara detectada
 * Caras humanas típicamente tienen aspect ratio entre 0.6 y 1.4
 */
const validateAspectRatio = (width, height) => {
  const aspectRatio = width / height;
  return aspectRatio >= 0.6 && aspectRatio <= 1.4;
};

/**
 * Valida el tamaño mínimo de la cara detectada
 * Debe ser al menos 100x100px para asegurar calidad
 */
const validateMinimumSize = (width, height) => width >= 100 && height >= 100;

/**
 * Calcula el área de crop óptima ajustándose a los límites de la imagen
 * Intenta hacer un crop 2.5x (más amplio para incluir cabello y hombros)
 * Se ajusta automáticamente si la cara está cerca del borde
 */
const calculateCropArea = (originX, originY, width, height, imgWidth, imgHeight) => {
  const minMargin = 10; // Margen mínimo de seguridad (10px)
  const idealExpansionFactor = 2.5; // Factor de expansión (2.5x incluye cabello completo y hombros)

  // Calcular el crop ideal (2.5x)
  let cropWidth = width * idealExpansionFactor;
  let cropHeight = height * idealExpansionFactor;
  let cropX = originX - (width / 2);
  let cropY = originY - (height / 2);

  // Ajustar horizontalmente si se sale de los límites
  const maxCropWidth = imgWidth - (2 * minMargin);
  if (cropWidth > maxCropWidth) {
    cropWidth = maxCropWidth;
    cropX = minMargin;
  } else {
    // Ajustar posición X si se sale por la izquierda
    if (cropX < minMargin) {
      cropX = minMargin;
    }
    // Ajustar posición X si se sale por la derecha
    if (cropX + cropWidth > imgWidth - minMargin) {
      cropX = imgWidth - minMargin - cropWidth;
    }
  }

  // Ajustar verticalmente si se sale de los límites
  const maxCropHeight = imgHeight - (2 * minMargin);
  if (cropHeight > maxCropHeight) {
    cropHeight = maxCropHeight;
    cropY = minMargin;
  } else {
    // Ajustar posición Y si se sale por arriba
    if (cropY < minMargin) {
      cropY = minMargin;
    }
    // Ajustar posición Y si se sale por abajo
    if (cropY + cropHeight > imgHeight - minMargin) {
      cropY = imgHeight - minMargin - cropHeight;
    }
  }

  // Asegurar que el crop sea cuadrado (tomar el menor de width/height)
  const finalCropSize = Math.min(cropWidth, cropHeight);

  // Calcular el centro REAL de la cara detectada
  // originX/originY son la esquina superior izquierda, no el centro
  const centerX = originX + (width / 2);
  let centerY = originY + (height / 2);

  // Ajuste para compensar accesorios en la cabeza (turbantes, gorros, sombreros)
  // MediaPipe incluye estos en el bounding box, desplazando el centro hacia arriba
  // Desplazamos el centro 5% hacia abajo para centrarlo mejor en ojos/nariz
  // (Reducido de 10% a 5% para dar más espacio al cabello arriba)
  const headAccessoryCompensation = height * 0.05;
  centerY += headAccessoryCompensation;

  let finalCropX = centerX - (finalCropSize / 2);
  let finalCropY = centerY - (finalCropSize / 2);

  // Ajustar si se sale de los límites después de hacer cuadrado
  if (finalCropX < minMargin) finalCropX = minMargin;
  if (finalCropY < minMargin) finalCropY = minMargin;
  if (finalCropX + finalCropSize > imgWidth - minMargin) {
    finalCropX = imgWidth - minMargin - finalCropSize;
  }
  if (finalCropY + finalCropSize > imgHeight - minMargin) {
    finalCropY = imgHeight - minMargin - finalCropSize;
  }

  return {
    x: finalCropX,
    y: finalCropY,
    width: finalCropSize,
    height: finalCropSize,
    expansionFactor: (finalCropSize / width).toFixed(2),
  };
};

/**
 * Detecta una cara en una imagen usando MediaPipe BlazeFace
 * @param {HTMLImageElement} image - Elemento de imagen a analizar
 * @returns {Object|null} - Coordenadas de la cara o null si hay error
 */
const FaceDetection = async (image) => {
  try {
    // Inicializar detector
    const detector = await initDetector();

    // IMPORTANTE: Usar dimensiones naturales de la imagen, no las renderizadas (CSS)
    // El elemento <img> puede tener width/height CSS diferentes al tamaño real
    const imgWidth = image.naturalWidth || image.width;
    const imgHeight = image.naturalHeight || image.height;

    // Crear un canvas temporal con las dimensiones naturales
    // Esto asegura que MediaPipe detecte en la imagen original, no en la versión redimensionada
    const canvas = document.createElement('canvas');
    canvas.width = imgWidth;
    canvas.height = imgHeight;
    const ctx = canvas.getContext('2d');
    ctx.drawImage(image, 0, 0, imgWidth, imgHeight);

    // Detectar caras en el canvas (imagen a tamaño real)
    const detections = detector.detect(canvas);

    if (process.env.NODE_ENV === 'development') {
      console.log('[FaceDetection] Dimensiones de imagen', {
        rendered: `${image.width}x${image.height}px`,
        natural: `${imgWidth}x${imgHeight}px`,
        usingNatural: imgWidth !== image.width || imgHeight !== image.height,
      });
    }

    // Validación 1: Verificar que se detectó al menos una cara
    if (!detections || detections.detections.length === 0) {
      console.warn('[FaceDetection] No se detectó ninguna cara en la imagen');
      return {
        error: 'NO_FACE_DETECTED',
        message: 'No se detectó ninguna cara en la imagen. Asegúrese de que la cara esté visible y bien iluminada.',
      };
    }

    // Validación 2: Verificar que solo hay una cara
    if (detections.detections.length > 1) {
      console.warn(
        `[FaceDetection] Se detectaron ${detections.detections.length} caras. Solo se permite una persona por foto.`,
      );

      // Verificar si hay una cara dominante (score mucho mayor que las demás)
      const sorted = detections.detections.sort(
        (a, b) => b.categories[0].score - a.categories[0].score,
      );

      const mainFace = sorted[0];
      const secondFace = sorted[1];

      // Si la segunda cara tiene score > 70% de la principal, rechazar
      if (secondFace.categories[0].score > mainFace.categories[0].score * 0.7) {
        console.error(
          '[FaceDetection] Múltiples caras con confianza similar detectadas',
          {
            mainScore: mainFace.categories[0].score,
            secondScore: secondFace.categories[0].score,
          },
        );
        return {
          error: 'MULTIPLE_FACES',
          message: 'Se detectaron múltiples personas en la foto. Solo debe aparecer UNA persona.',
        };
      }

      console.warn(
        '[FaceDetection] Se detectó cara dominante, ignorando detecciones secundarias',
      );
    }

    // Obtener la cara con mayor confianza
    const face = detections.detections[0];
    const confidence = face.categories[0].score;

    // Validación 3: Verificar score de confianza mínimo
    if (confidence < 0.85) {
      console.warn(
        `[FaceDetection] Confianza muy baja: ${(confidence * 100).toFixed(1)}%`,
      );
      return {
        error: 'LOW_CONFIDENCE',
        message: 'La calidad de detección facial es muy baja. Asegúrese de que la cara esté bien visible, centrada y con buena iluminación.',
      };
    }

    // Extraer coordenadas del bounding box
    const {
      originX, originY, width, height,
    } = face.boundingBox;

    // Validación 4: Verificar tamaño mínimo
    if (!validateMinimumSize(width, height)) {
      console.error('[FaceDetection] La cara detectada es muy pequeña', {
        width,
        height,
      });
      return {
        error: 'FACE_TOO_SMALL',
        message: 'La cara en la foto es muy pequeña. Acérquese más o tome una foto con mejor resolución.',
      };
    }

    // Validación 5: Verificar relación de aspecto
    if (!validateAspectRatio(width, height)) {
      console.error(
        '[FaceDetection] Relación de aspecto inválida (no parece una cara humana)',
        {
          width,
          height,
          aspectRatio: (width / height).toFixed(2),
        },
      );
      return {
        error: 'INVALID_ASPECT_RATIO',
        message: 'La foto no parece contener una cara válida. Verifique que la imagen sea correcta.',
      };
    }

    // Calcular área de crop óptima (se ajusta automáticamente si está cerca del borde)
    const cropArea = calculateCropArea(
      originX,
      originY,
      width,
      height,
      imgWidth,
      imgHeight,
    );

    // Validación 6: Verificar que el crop mínimo sea razonable
    // El crop debe ser al menos 1.2x el tamaño de la cara
    if (cropArea.width < width * 1.2) {
      console.error(
        '[FaceDetection] La imagen es demasiado pequeña o la cara ocupa toda la imagen',
        {
          faceSize: `${Math.round(width)}x${Math.round(height)}px`,
          imageSize: `${imgWidth}x${imgHeight}px`,
          cropSize: `${Math.round(cropArea.width)}x${Math.round(cropArea.height)}px`,
          expansionFactor: cropArea.expansionFactor,
        },
      );
      return {
        error: 'IMAGE_TOO_SMALL',
        message: 'La imagen es demasiado pequeña o la cara ocupa toda la foto. Use una imagen más grande o aléjese un poco.',
      };
    }

    // Log de éxito
    if (process.env.NODE_ENV === 'development') {
      console.log('[FaceDetection] Cara detectada exitosamente', {
        confidence: `${(confidence * 100).toFixed(1)}%`,
        faceBoundingBox: {
          topLeft: `(${Math.round(originX)}, ${Math.round(originY)})`,
          size: `${Math.round(width)}x${Math.round(height)}px`,
        },
        cropArea: {
          topLeft: `(${Math.round(cropArea.x)}, ${Math.round(cropArea.y)})`,
          size: `${Math.round(cropArea.width)}x${Math.round(cropArea.height)}px`,
          expansionFactor: `${cropArea.expansionFactor}x`,
        },
      });
    }

    // Retornar coordenadas para Cropper.js
    return {
      x: cropArea.x,
      y: cropArea.y,
      width: cropArea.width,
      height: cropArea.height,
      confidence,
      expansionFactor: cropArea.expansionFactor,
    };
  } catch (error) {
    console.error('[FaceDetection] Error al detectar cara:', error);
    return {
      error: 'PROCESSING_ERROR',
      message: 'Ocurrió un error al procesar la foto. Por favor, intente con otra imagen.',
    };
  }
};

export default FaceDetection;
export { initDetector };
